import { useQueryClient } from '@tanstack/react-query'
import pluralize from 'pluralize'
import { useState } from 'react'
import { toast } from 'sonner'

import { IllustrationName } from '@/components/icons/Illustration'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CacheKeys, invalidateApplicationSummary } from '../../lib/cache'
import { cn } from '../../lib/utils'
import { addDriver, designateOwnerDriver, type Driver, editDriver, useDrivers } from '../../models/driver'
import { ChatActionWithValue } from '../../models/insuranceApplication'
import { isFleetReadonly, useCurrentUser } from '../../models/user'
import { createEmptyDriver, EditDriverModal, type OwnerAsDriver } from '../DriversTable'
import { IllustratedRow } from './ConfirmInfoCard'

export interface ChatOwnerOperatorDriverProps {
  /** The one driver on file, edited in place; without it the modal adds the owner. */
  driverId: string | null
  /** The owner's own answers, for a driver not yet on file. */
  prefill: OwnerAsDriver | null
  orgId: string | undefined
  handleSubmit?: (data: ChatActionWithValue | FormData) => void
  readonly?: boolean
}

/** One driver, one truck: the drivers step as the Add Driver modal alone, opened on arrival. */
export function ChatOwnerOperatorDriver({
  driverId,
  prefill,
  orgId,
  handleSubmit,
  readonly,
}: ChatOwnerOperatorDriverProps) {
  const queryClient = useQueryClient()
  const { drivers, isLoading, refetch } = useDrivers({ orgId, shouldFetchAllPages: true })
  const { user } = useCurrentUser({ enabled: false })
  const isDisabled = readonly || isFleetReadonly(user)
  const [isOpen, setIsOpen] = useState(!isDisabled)
  const [newDriver] = useState<Driver>(() => ({ ...createEmptyDriver(), ...prefill, isBusinessOwner: true }))

  // Set once a save lands, so a retry after a failed designation edits that driver instead of adding another.
  const [savedId, setSavedId] = useState(driverId)
  const existing = driverId ? drivers.find((driver) => driver.id === driverId) : undefined
  const driver = existing ?? newDriver
  // A driver on file that didn't load must never open as a blank form, or saving would blank it.
  const failedToLoad = !isLoading && !!driverId && !existing

  async function save(entered: Driver) {
    if (!orgId) return
    const saved = savedId ? await editDriver(orgId, savedId, entered) : await addDriver(orgId, entered)
    setSavedId(saved.id)
    // A cleared email skips the designation, so an email that's taken never traps them in the modal.
    const designated = entered.email ? await designate(orgId, saved.id, entered.email) : true
    // After both writes, so the roster and summary pick up the owner flag and synced profile.
    queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
    invalidateApplicationSummary(queryClient, orgId)
    if (!designated) return
    setIsOpen(false)
    handleSubmit?.({
      content: {
        component: 'ADD_DRIVERS',
        content: ``,
        props: { edit: true, disableEnteringAnimation: true },
      },
      value: { name: 'ADD_DRIVERS' },
    })
  }

  return (
    <>
      <div
        className={cn(
          'mt-6 flex w-full cursor-pointer items-center justify-end gap-2 font-semibold',
          isDisabled && 'cursor-auto opacity-50'
        )}
        onClick={isDisabled ? undefined : () => setIsOpen(true)}
      >
        <Card className="w-full bg-transparent">
          <CardContent>
            <IllustratedRow
              illustration={IllustrationName.DRIVER}
              label={
                failedToLoad
                  ? 'Couldn’t load your driver'
                  : isLoading
                    ? 'Loading drivers...'
                    : `${drivers.length} active ${pluralize('driver', drivers.length)}`
              }
              action={
                !isDisabled &&
                (failedToLoad ? (
                  <Button variant="outline" onClick={() => void refetch()}>
                    Try again
                  </Button>
                ) : (
                  <Button>{existing ? 'Review' : 'Add'}</Button>
                ))
              }
            />
          </CardContent>
        </Card>
      </div>
      {isOpen && orgId && !isLoading && !failedToLoad && (
        <EditDriverModal
          driver={driver}
          existingDrivers={drivers.filter((other) => other.id !== driver.id)}
          onClose={() => setIsOpen(false)}
          onSubmit={save}
          isNew={!existing}
          hideOwnerToggle
          saveOnClose
        />
      )}
    </>
  )
}

async function designate(orgId: string, driverId: string, email: string): Promise<boolean> {
  try {
    await designateOwnerDriver(orgId, driverId, { email })
    return true
  } catch (err) {
    toast.error(designationError(err))
    return false
  }
}

/** The endpoint's own message, or the one field it rejected (a duplicate email). */
function designationError(err: unknown): string {
  const data = (err as { response?: { data?: { error?: string; email?: string | string[] } } }).response?.data
  const field = Array.isArray(data?.email) ? data.email[0] : data?.email
  return data?.error ?? field ?? 'Saved the driver, but not as the business owner'
}
